<template>
    <div>
        <v-card-text v-if="!responses || !responses.length">
            <v-alert text type="info">
                {{ $t('Ei vastauksia') }}
            </v-alert>
        </v-card-text>

        <v-card-text v-else>
            <v-form>
                <div v-for="(response, idx) in responses" :key="`${response.field_id}-${idx}`" class="mb-4">
                    <div class="text--primary font-weight-medium mb-1">
                        {{ response.label || $t('Kysymys') }}
                    </div>

                    <!-- Text (short) -->
                    <v-text-field v-if="isShortText(response)" dense outlined disabled
                        :value="formatDisplayValue(response)" :placeholder="$t('Ei vastausta')" hide-details />

                    <!-- Text (long) -->
                    <v-textarea v-else-if="isLongText(response)" dense outlined disabled auto-grow rows="2"
                        :value="formatDisplayValue(response)" :placeholder="$t('Ei vastausta')" hide-details />

                    <!-- Radio (single choice) -->
                    <div v-else-if="isRadio(response)">
                        <v-radio-group class="mt-0 pt-0" :value="radioValue(response)" disabled hide-details>
                            <v-radio v-for="opt in radioOptions(response)" :key="opt.value" :label="opt.label"
                                :value="opt.value" class="my-0" />
                        </v-radio-group>

                        <template v-if="!radioOptions(response).length">
                            <i> {{ $t('Ei valintaa') }}</i>
                        </template>
                    </div>

                    <!-- Checkbox (multi choice) -->
                    <div v-else-if="isCheckbox(response)">
                        <div v-if="checkboxLabels(response).length">
                            <v-checkbox v-for="(label, i) in checkboxLabels(response)"
                                :key="`${response.field_id}-${i}`" :label="label" :input-value="true" disabled dense
                                hide-details class="my-0" />
                        </div>
                        <template v-if="!radioOptions(response).length">
                            <i> {{ $t('Ei valintaa') }}</i>
                        </template>
                    </div>

                    <!-- Fallback -->
                    <v-text-field v-else dense outlined readonly :value="formatDisplayValue(response)"
                        :placeholder="$t('Ei vastausta')" hide-details />
                </div>
            </v-form>
        </v-card-text>
    </div>
</template>

<script>
import { getDate } from "@/@core/utils/datetime_helpers";

export default {
    name: 'FormSubmissionRenderer',
    props: {
        // Expected shape: feedback.form_submission.responses
        // Each response should ideally include:
        // - field_id
        // - label
        // - display_value
        // - value (raw)
        responses: {
            type: Array,
            default: () => [],
        },
    },
    setup() {
        // Matches backend `FormField.FieldType` enum
        const FIELD_TYPES = {
            SHORT_TEXT: 1,
            LONG_TEXT: 2,
            RADIO: 3,
            CHECKBOX: 4,
        };

        const isShortText = (response) => response?.field_type === FIELD_TYPES.SHORT_TEXT;
        const isLongText = (response) => response?.field_type === FIELD_TYPES.LONG_TEXT;
        const isRadio = (response) => response?.field_type === FIELD_TYPES.RADIO;
        const isCheckbox = (response) => response?.field_type === FIELD_TYPES.CHECKBOX;

        const formatDisplayValue = (response) => {
            // Date fields: show local date without time
            if (response?.datatype === 'date') {
                const raw = response?.value;
                if (!raw) return '';
                try {
                    // Accept ISO string, Date, or timestamp-ish
                    return getDate(raw);
                } catch (e) {
                    return String(raw);
                }
            }

            if (response?.display_value !== undefined && response?.display_value !== null) {
                return String(response.display_value);
            }

            // Backwards-compat: if we only have the raw `value` field
            const raw = response?.value;
            if (Array.isArray(raw)) {
                return raw.join(', ');
            }
            if (raw === undefined || raw === null || raw === '') {
                return '';
            }
            return String(raw);
        };

        const selectedLabels = (response) => {
            // If backend later returns richer data, prefer it.
            if (Array.isArray(response?.selected_options)) {
                return response.selected_options
                    .map(o => o?.label)
                    .filter(Boolean);
            }

            const dv = response?.display_value;
            if (dv === undefined || dv === null || dv === '') {
                return [];
            }

            // Best-effort parsing; backend currently sends display_value as comma-separated labels.
            return String(dv).split(', ').filter(Boolean);
        };

        const radioOptions = (response) => {
            return selectedLabels(response).map(label => ({
                value: label,
                label,
            }));
        };

        const radioValue = (response) => {
            const opts = radioOptions(response);
            return opts.length ? opts[0].value : null;
        };

        const checkboxLabels = (response) => selectedLabels(response);

        return {
            formatDisplayValue,
            isShortText,
            isLongText,
            isRadio,
            isCheckbox,
            radioOptions,
            radioValue,
            checkboxLabels,
        };
    },
};
</script>

<style scoped></style>
